import { Icon, XOutlineIcon } from '@fountain/ripple/icon';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { useStyles as useDialogStyles } from '../styles';
import messages from './messages';

interface RejectOpeningDialogProps {
  /**
   * Named in the body, so the dialog says which row it came from. Read only when
   * one opening is being rejected — several cannot be named in a sentence.
   */
  openingName: string | null;
  /** How many openings this rejection covers. One, from a single row. */
  count: number;
  /**
   * Whether any request this rejection covers holds an edit to a live opening.
   * Such an opening stays live, so the body cannot say it stays unpublished.
   */
  holdsEdit: boolean;
  submitting: boolean;
  /** What the server said when a decision was refused, verbatim. */
  error?: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const TITLE_ID = 'reject-opening-title';
const REASON_FIELD_ID = 'reject-opening-reason';

/**
 * Why an opening is being held, collected before the rejection is recorded
 * (AXHE-3934).
 *
 * ## A modal, where the offer panel uses a two-step inline field
 *
 * `DecisionFooter` opens its reason field in place, because it lives in a
 * drawer that has room for one. A table cell does not, and the reason is
 * mandatory — the server refuses a rejection without one — so it cannot be
 * skipped or deferred. That leaves a modal: one decision, one field, one outcome,
 * which is what the pattern is for.
 *
 * ## One dialog for one row and for many
 *
 * A bulk rejection asks the same question and records the same field, against
 * more rows. `count` is what switches the sentences; everything else — the
 * mandatory reason, the outlined confirm, the chrome — is the same, because a
 * second dialog is a second chance for the two to disagree about whether the
 * reason is required.
 *
 * The chrome comes from the rules tab's own dialog styles rather than being
 * restated: `DeleteRuleDialog` is the surface this has to line up with, and its
 * styles carry the reasoning for every token in them.
 *
 * ## Reject is not the primary control here either
 *
 * A rejection short-circuits the whole chain (`Approvals::RecordDecision`), so
 * the confirm is outlined in the error role rather than filled, and Cancel keeps
 * the neutral box. `constraint-anti-pattern-guard` prohibits a destructive action
 * in the primary visual position.
 */
export const RejectOpeningDialog: React.FC<RejectOpeningDialogProps> = ({
  openingName,
  count,
  holdsEdit,
  submitting,
  error,
  onCancel,
  onConfirm,
}) => {
  const styles = useDialogStyles();
  const intl = useIntl();
  const theme = useTheme();
  // The one thing the FDS shell did for free. At 448px the dialog is wider than
  // the smallest phones, and a modal that overflows puts its actions off the edge.
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [reason, setReason] = useState('');
  const canConfirm = reason.trim().length > 0;

  return (
    <Dialog
      aria-labelledby={TITLE_ID}
      classes={{ paper: styles.dialogPaper }}
      fullScreen={fullScreen}
      onClose={onCancel}
      open
    >
      <DialogTitle className={styles.dialogTitle} disableTypography>
        <Typography id={TITLE_ID} variant="subtitle2">
          <FormattedMessage
            {...(count > 1
              ? messages.rejectOpeningsTitle
              : messages.rejectOpeningTitle)}
            values={{ count }}
          />
        </Typography>
        <IconButton
          aria-label={intl.formatMessage(globalMessages.close)}
          className={styles.dialogClose}
          onClick={onCancel}
          size="small"
        >
          <Icon icon={XOutlineIcon} size="md" color="inherit" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent} dividers>
        {/* Which opening, or how many. The dialog is opened from one row of
            many and by the time it is over the rows are behind it — and one
            reason is recorded against every one of them, which is the thing a
            reader has to agree to before typing it. */}
        <Typography className={styles.dialogSentence} variant="body2">
          {count > 1 ? (
            <FormattedMessage
              {...(holdsEdit
                ? messages.rejectOpeningsHeldEditBody
                : messages.rejectOpeningsBody)}
              values={{ count }}
            />
          ) : (
            <FormattedMessage
              {...(holdsEdit
                ? messages.rejectOpeningEditBody
                : messages.rejectOpeningBody)}
              values={{ opening: openingName ?? '' }}
            />
          )}
        </Typography>

        {/* Labelled by a real `<label>` rather than a placeholder, and the helper
            is wired through `aria-describedby` so the "the requester sees this"
            warning is announced with the field rather than after it. */}
        <TextField
          autoFocus
          fullWidth
          helperText={intl.formatMessage(messages.rejectOpeningReasonHelper)}
          // Explicit, because MUI v4 mints no id of its own: without one the
          // `InputLabel` carries no `htmlFor` and the field carries no `id`, so
          // the visible label is not the field's accessible name at all. Static
          // is safe — one dialog is open at a time, so one reason field exists.
          id={REASON_FIELD_ID}
          label={intl.formatMessage(messages.rejectOpeningReasonLabel)}
          multiline
          rows={3}
          rowsMax={6}
          value={reason}
          variant="outlined"
          onChange={event => setReason(event.target.value)}
        />

        {/* The server's own sentence, verbatim: it distinguishes "already decided
            by somebody else" from "not your level", and a generic fallback would
            collapse two different next actions into one. */}
        {error && (
          <Typography
            className={styles.dialogError}
            role="alert"
            variant="body2"
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          className={styles.dialogButton}
          disabled={submitting}
          variant="outlined"
          onClick={onCancel}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        {/* The mandatory reason enforced on the control rather than on submit: an
            approver should not be able to press this and then be told the reason
            was required. */}
        <Button
          className={styles.dialogDeleteButton}
          disabled={submitting || !canConfirm}
          variant="outlined"
          onClick={() => onConfirm(reason.trim())}
        >
          <FormattedMessage
            {...(count > 1
              ? messages.confirmRejectOpenings
              : messages.confirmRejectOpening)}
            values={{ count }}
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
